import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "saor_pwa_install_dismissed_at";
const DISMISS_DURATION_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

/**
 * Non-intrusive PWA install banner.
 * - Captures `beforeinstallprompt` (Chrome/Android/Edge) and defers it.
 * - Shows a subtle bottom banner inviting the user to install.
 * - Auto-dismisses when already installed (display-mode: standalone).
 * - Respects user dismissal for 14 days via localStorage.
 * - Hidden on iOS (Safari doesn't support the prompt API — the user adds manually).
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // If already running as an installed PWA, skip
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (navigator.standalone === true) return; // iOS home-screen app

    // Respect recent dismissal
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < DISMISS_DURATION_MS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (_) {}
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[9998] mx-auto max-w-sm rounded-2xl border border-primary/20 bg-card/95 p-4 shadow-2xl backdrop-blur-xl animate-in slide-in-from-bottom-5 fade-in duration-300"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-[Outfit] text-sm font-medium">Installer Price</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Accédez à vos courses d'un geste depuis votre écran d'accueil.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              className="rounded-full bg-primary px-4 text-xs hover:bg-primary/90"
              onClick={install}
              data-testid="pwa-install-btn"
            >
              Installer
            </Button>
            <button
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={dismiss}
              data-testid="pwa-install-later"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="flex-none rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label="Fermer"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
